import React, { useEffect, useRef } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import WhyChooseUs from './WhyChooseUs';
import './sticky-header-desktop.css';
import Header from '../Header';
import Footer from '../Footer';
import OurTeam from './OurTeam';
import RootsWings from './RootsWings';
import OurJourney from './OurJourney';
import LSG from './LSG';
import ThreeEye from './ThreeEye';
import ClientTestimonials from './ClientTestimonials';
import Hero2, { Hero2IconsRow, HERO2_SECTIONS } from './Hero2';

const WhyMaxiom = () => {
  const location = useLocation();
  const { section } = useParams();
  const typesRef = useRef(null);
  const aboutUsRef = React.useRef(null);
  const lsgRef = React.useRef(null);
  const ourTeamRef = React.useRef(null);
  // Refs for sections for highlighting
  const rootsWingsRef = React.useRef(null);
  const threeEyeRef = React.useRef(null);
  const lsgIconRef = lsgRef; // alias for clarity

  const [showStickyIcons, setShowStickyIcons] = React.useState(false);
  const [activeSection, setActiveSection] = React.useState(null);

  // Hide global header when sticky bar is active
  React.useEffect(() => {
    if (showStickyIcons) {
      document.body.classList.add('whymaxiom-sticky-active');
    } else {
      document.body.classList.remove('whymaxiom-sticky-active');
    }
  }, [showStickyIcons]);

  // Desktop-only sticky header scroll logic with debug output
  // Debounced scroll handler for sticky header and active section
  React.useEffect(() => {
    let scrollTimeout = null;
    const handleScroll = () => {
      if (scrollTimeout) clearTimeout(scrollTimeout);
      scrollTimeout = setTimeout(() => {
        const width = window.innerWidth;
        if (width < 768) {
          setShowStickyIcons(false);
          setActiveSection(null);
          return;
        }
        if (!aboutUsRef.current || !lsgRef.current || !ourTeamRef.current || !rootsWingsRef.current || !threeEyeRef.current) {
          setShowStickyIcons(false);
          setActiveSection(null);
          return;
        }
        const aboutUsRect = aboutUsRef.current.getBoundingClientRect();
        const lsgRect = lsgRef.current.getBoundingClientRect();
        const ourTeamRect = ourTeamRef.current.getBoundingClientRect();
        // Sticky header logic
        const shouldShow = (
          aboutUsRect.bottom <= 0 &&
          !(lsgRect.bottom <= 0 && ourTeamRect.top <= window.innerHeight)
        );
        setShowStickyIcons(shouldShow);
        // Active section highlight logic
        const sectionRects = [
          { id: 'our-investment-philosophy', rect: rootsWingsRef.current.getBoundingClientRect() },
          { id: 'threeEye', rect: threeEyeRef.current.getBoundingClientRect() },
          { id: 'lsg', rect: lsgRef.current.getBoundingClientRect() },
        ];
        // Find the section whose top is closest to (but not greater than) the sticky bar (top: 0 + sticky bar height)
        const stickyBarHeight = 60; // px, match sticky-header-margin-desktop
        const visibleSections = sectionRects.filter(s => s.rect.top <= stickyBarHeight + 16); // allow small offset
        let current = null;
        if (visibleSections.length > 0) {
          current = visibleSections.reduce((a, b) => (a.rect.top > b.rect.top ? a : b)).id;
        } else {
          // If none are above sticky bar, highlight the first section ahead
          const ahead = sectionRects.find(s => s.rect.top > stickyBarHeight);
          current = ahead ? ahead.id : null;
        }
        setActiveSection(current);
      }, 40); // debounce by 40ms
    };
    window.addEventListener('scroll', handleScroll);
    window.addEventListener('resize', handleScroll);
    setTimeout(handleScroll, 100);
    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleScroll);
      if (scrollTimeout) clearTimeout(scrollTimeout);
    };
  }, []);

  // Icon click handler
  const handleIconClick = (sectionId) => {
    const el = document.getElementById(sectionId);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };


  useEffect(() => {
    // Function to scroll to section
    const scrollToSection = () => {
      const targetSection = section || location.hash.replace('#', '');
      if (targetSection) {
        setTimeout(() => {
          const element = document.getElementById(targetSection);
          if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        }, 100);
      }
    };

    // Call on mount and when location changes
    scrollToSection();

    // Listen for hash changes (for backward compatibility)
    window.addEventListener('hashchange', scrollToSection);
    return () => window.removeEventListener('hashchange', scrollToSection);
  }, [location, section]);
  
    useEffect(() => {
      // Check if there's state with focusSection
      if (location.state && location.state.focusSection) {
        const sectionId = location.state.focusSection;
        const element = document.getElementById(sectionId);
  
        if (element && location.state.skipScrolling) {
          // Use scrollIntoView with a behavior of 'auto' to jump directly without animation
          element.scrollIntoView({ behavior: 'auto' });
  
          // Store reference to prevent re-scrolling on subsequent renders
          typesRef.current = element;
        }
      }
    }, [location]);

  return (
    <div className="why-maxiom-container">
      {/* Sticky icon header for desktop only */}
      {/* Sticky icon header for desktop only with fade/slide transition */}
      <div
        className={`sticky-icons-bar pointer-events-none fixed top-0 left-0 w-full z-[1000] transition-all duration-300 ease-in-out ${showStickyIcons ? 'opacity-100 translate-y-0 pointer-events-auto' : 'opacity-0 -translate-y-4 pointer-events-none'}`}
        style={{background: 'white', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', minHeight: '56px', maxHeight: '60px'}}
        aria-hidden={!showStickyIcons}
      >
        <Hero2IconsRow onIconClick={handleIconClick} compact={true} activeSection={activeSection} />
      </div>
      {/* Only show Header when sticky icons are NOT shown */}
      {!showStickyIcons && <Header />}
      <div className="why-maxiom-content">
        <div id="about-us">
          <div ref={aboutUsRef}>
            <WhyChooseUs />
          </div>
          <Hero2/>
        </div>
        <div id="our-investment-philosophy" ref={rootsWingsRef}>
          <RootsWings />
        </div>
        <div id="threeEye" ref={threeEyeRef}>
          <ThreeEye />
        </div>
        <div id="lsg" ref={lsgRef}>
          <LSG />
        </div>
        <div id="ourTeam" ref={ourTeamRef}>
          <OurTeam />
        </div>
        <div id="ourJourney">
          <OurJourney />
        </div>
        <div id="clientTestimonials">
          <ClientTestimonials/>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default WhyMaxiom;